import { Controller } from "@hotwired/stimulus"
import { loadStripe } from "@stripe/stripe-js/pure"

// Connects to data-controller="checkout"
//
// Values:
//   publishable-key  – Stripe publishable key
//   client-secret    – SetupIntent client_secret (for adding a new card)
//
// Targets:
//   cardElement      – container div for the Stripe CardElement
//   newCardForm      – wrapper around the CardElement + submit button
//   savedCards       – wrapper around the saved-card radio list
//   submitButton     – the "Place Order" button
//   errorMessage     – span/div for inline error messages
//   paymentMethodId  – hidden input that carries the chosen payment_method id
//
export default class extends Controller {
  static targets = [
    "cardElement",
    "newCardForm",
    "savedCards",
    "submitButton",
    "errorMessage",
    "paymentMethodId"
  ]

  static values = {
    publishableKey: String,
    clientSecret: String
  }

  async connect() {
    this.stripe = await loadStripe(this.publishableKeyValue)
    this.submitting = false

    if (this.hasCardElementTarget && this.clientSecretValue) {
      this.mountCardElement()
    }

    // Auto-select the first saved card if present
    const firstRadio = this.element.querySelector('input[name="selected_card"]:checked')
    if (firstRadio && this.hasPaymentMethodIdTarget) {
      this.paymentMethodIdTarget.value = firstRadio.value
    }
  }

  mountCardElement() {
    this.elements = this.stripe.elements()

    this.cardElement = this.elements.create("card", {
      style: {
        base: {
          fontSize: "16px",
          color: "#32325d",
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          "::placeholder": { color: "#aab7c4" }
        },
        invalid: { color: "#fa755a", iconColor: "#fa755a" }
      }
    })
    this.cardElement.mount(this.cardElementTarget)
  }

  // Called when user clicks "Use a different card"
  showNewCardForm(event) {
    event.preventDefault()
    if (this.hasSavedCardsTarget) {
      this.savedCardsTarget.classList.add("hidden")
    }
    if (this.hasNewCardFormTarget) {
      this.newCardFormTarget.classList.remove("hidden")
    }
    // Deselect any saved card radio
    this.element.querySelectorAll('input[name="payment_method_id"]').forEach(r => r.checked = false)
    if (this.hasPaymentMethodIdTarget) {
      this.paymentMethodIdTarget.value = ""
    }
  }

  // Called when user clicks "Use saved card" (to go back)
  showSavedCards(event) {
    event.preventDefault()
    if (this.hasNewCardFormTarget) {
      this.newCardFormTarget.classList.add("hidden")
    }
    if (this.hasSavedCardsTarget) {
      this.savedCardsTarget.classList.remove("hidden")
    }
  }

  // Called when a saved-card radio is selected
  selectCard(event) {
    if (this.hasPaymentMethodIdTarget) {
      this.paymentMethodIdTarget.value = event.currentTarget.value
    }
  }

  // Intercept form submission
  async submit(event) {
    event.preventDefault()
    if (this.submitting) return
    this.submitting = true
    this.clearError()
    this.disableSubmit()

    // Check the hidden input first, then fall back to the checked radio
    let selectedMethodId = this.hasPaymentMethodIdTarget ? this.paymentMethodIdTarget.value : ""
    if (!selectedMethodId) {
      const checkedRadio = this.element.querySelector('input[name="selected_card"]:checked')
      if (checkedRadio) {
        selectedMethodId = checkedRadio.value
        if (this.hasPaymentMethodIdTarget) {
          this.paymentMethodIdTarget.value = selectedMethodId
        }
      }
    }

    if (selectedMethodId) {
      // Using an existing saved card — just submit the form
      this.element.closest("form").requestSubmit()
      return
    }

    // Adding a new card — confirm the SetupIntent with the CardElement
    try {
      const { setupIntent, error } = await this.stripe.confirmCardSetup(
        this.clientSecretValue,
        { payment_method: { card: this.cardElement } }
      )

      if (error) {
        this.showError(error.message)
        this.enableSubmit()
        this.submitting = false
        return
      }

      // SetupIntent succeeded — set the payment method and submit
      if (this.hasPaymentMethodIdTarget) {
        this.paymentMethodIdTarget.value = setupIntent.payment_method
      }
      this.element.closest("form").requestSubmit()
    } catch (err) {
      this.showError(err.message || "Something went wrong. Please try again.")
      this.enableSubmit()
      this.submitting = false
    }
  }

  // ---------- helpers ----------

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove("hidden")
    }
  }

  clearError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = ""
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  disableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.dataset.originalText = this.submitButtonTarget.textContent
      this.submitButtonTarget.textContent = "Processing…"
    }
  }

  enableSubmit() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = this.submitButtonTarget.dataset.originalText || "Place Order"
    }
  }
}
